import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ShieldCheck, Video, Calendar, Brain, ArrowRight, CheckCircle, Star, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import TutorCard from '../components/TutorCard';
import { testimonials } from '../mock';
import { tutorSearchService } from '../services/tutorSearchService';
import { tutorService } from '../services/tutorService';
import { TutorCardSkeleton } from '../components/ui/skeletons';

const Home = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [isFetchingClasses, setIsFetchingClasses] = useState(true);
  const [tutors, setTutors] = useState([]);
  const [tutorsLoading, setTutorsLoading] = useState(true);
  const [tutorsError, setTutorsError] = useState(null);

  const areas = [
    { id: 'lucknow', name: 'Lucknow' },
    { id: 'sitapur', name: 'Sitapur' },
    { id: 'barabanki', name: 'Barabanki' },
  ];

  useEffect(() => {
    fetchClasses();
    fetchTutors();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await tutorService.getClasses();
      setClasses(response.data || response || []);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    } finally {
      setIsFetchingClasses(false);
    }
  };

  const fetchTutors = async () => {
    try {
      setTutorsLoading(true);
      setTutorsError(null);
      const { tutors: tutorList } = await tutorSearchService.searchTutors({ per_page: 6 });
      setTutors(tutorList);
    } catch (error) {
      console.error('Failed to fetch tutors:', error);
      setTutorsError('Failed to load tutors. Please try again.');
    } finally {
      setTutorsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!selectedClassId) {
      return;
    }

    setSearchLoading(true);
    try {
      const filters = {};
      const classNumber = selectedClassId.replace(/\D/g, '');
      if (classNumber) filters.class = parseInt(classNumber);
      if (selectedArea) filters.area = selectedArea;

      const { tutors: results } = await tutorSearchService.searchTutors(filters);

      navigate('/search-results', {
        state: {
          results: results,
          filters: {
            classId: selectedClassId,
            className: classes.find(c => c.id?.toString() === selectedClassId)?.name || '',
            area: selectedArea,
            areaName: areas.find(a => a.id === selectedArea)?.name || ''
          }
        }
      });
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-teal-50 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <div className="space-y-8 z-10">
              <div className="inline-block">
                <span className="px-4 py-2 bg-blue-100 text-blue-700 text-sm font-medium rounded-full border border-blue-200">
                  #1 Tutor Marketplace in India
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Find Verified
                <span className="bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent"> Home Tutors </span>
                Near You
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Connect with qualified, background-verified tutors for personalized learning. Book a free demo class today!
              </p>

              {/* Search Bar */}
              <div className="bg-white p-4 rounded-2xl shadow-xl border border-gray-100">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <Select value={selectedClassId} onValueChange={setSelectedClassId}>
                      <SelectTrigger className="border-gray-200 h-12">
                        <SelectValue placeholder="Select Class" />
                      </SelectTrigger>
                      <SelectContent>
                        {isFetchingClasses ? (
                          <SelectItem value="_loading" disabled>Loading classes...</SelectItem>
                        ) : (
                          classes.map((cls) => (
                            <SelectItem key={cls.id} value={cls.id?.toString()}>
                              {cls.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Select value={selectedArea} onValueChange={setSelectedArea}>
                      <SelectTrigger className="border-gray-200 h-12">
                        <SelectValue placeholder="Select Area" />
                      </SelectTrigger>
                      <SelectContent>
                        {areas.map((area) => (
                          <SelectItem key={area.id} value={area.id}>
                            {area.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleSearch}
                    disabled={searchLoading || !selectedClassId}
                    className="bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white h-12 px-8 shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
                  >
                    {searchLoading ? (
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    ) : (
                      <Search className="h-5 w-5 mr-2" />
                    )}
                    Search Tutors
                  </Button>
                </div>
              </div>

              {/* Stats */}
              {/* <div className="flex flex-wrap gap-8 pt-4">
                <div>
                  <div className="text-3xl font-bold text-blue-600">10,000+</div>
                  <div className="text-sm text-gray-600">Verified Tutors</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-teal-600">50,000+</div>
                  <div className="text-sm text-gray-600">Happy Students</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-600">4.8/5</div>
                  <div className="text-sm text-gray-600">Average Rating</div>
                </div>
              </div> */}
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-teal-400 rounded-3xl blur-3xl opacity-20"></div>
              <img
                src="https://images.unsplash.com/photo-1588873281272-14886ba1f737?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxvbmxpbmUlMjB0dXRvcmluZ3xlbnwwfHx8fDE3NjI0Mzk3MTh8MA&ixlib=rb-4.1.0&q=85"
                alt="Online Tutoring"
                className="relative rounded-3xl shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Why Choose Us</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4">Everything You Need for Success</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Comprehensive features designed to make learning effective and enjoyable
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: ShieldCheck,
                title: "Verified Tutors",
                description: "All tutors undergo rigorous background checks and skill assessments",
                gradient: "from-blue-500 to-blue-600"
              },
              {
                icon: Video,
                title: "Demo Class Booking",
                description: "Try before you commit with free demo classes from any tutor",
                gradient: "from-teal-500 to-teal-600"
              },
              {
                icon: Calendar,
                title: "Weekly Study Plans",
                description: "Personalized study schedules tailored to your learning goals",
                gradient: "from-blue-500 to-teal-500"
              },
              {
                icon: Brain,
                title: "AI-Generated Quizzes",
                description: "Smart practice tests that adapt to your learning pace",
                gradient: "from-teal-500 to-blue-500"
              }
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="p-8 hover:shadow-xl transition-all duration-300 group border-gray-100 bg-white">
                  <div className={`w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Simple Process</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4">How It Works</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Get started with PriEdu in three simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connection Lines (Desktop) */}
            <div className="hidden md:block absolute top-24 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-blue-300 via-teal-300 to-blue-300"></div>

            {[
              {
                step: "01",
                title: "Search & Compare",
                description: "Browse through verified tutors, check their profiles, ratings, and reviews. Filter by subject, class, and location."
              },
              {
                step: "02",
                title: "Book Demo Class",
                description: "Schedule a free demo class with your preferred tutor. Experience their teaching style before committing."
              },
              {
                step: "03",
                title: "Start Learning",
                description: "Begin your personalized learning journey with weekly study plans and AI-powered practice quizzes."
              }
            ].map((item, index) => (
              <div key={index} className="relative">
                <Card className="p-8 text-center bg-white hover:shadow-xl transition-all duration-300 border-gray-100">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg relative z-10">
                    <span className="text-2xl font-bold text-white">{item.step}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Tutors Section */}
      <section id="tutors" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Meet Our Experts</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4">Top Rated Tutors</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Learn from the best educators with proven track records
            </p>
          </div>

          {tutorsLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <TutorCardSkeleton key={i} />
              ))}
            </div>
          ) : tutorsError ? (
            <div className="text-center py-12">
              <Card className="p-8 max-w-md mx-auto">
                <p className="text-red-600 mb-4">{tutorsError}</p>
                <Button onClick={fetchTutors} variant="outline" className="gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Retry
                </Button>
              </Card>
            </div>
          ) : tutors.length === 0 ? (
            <div className="text-center py-12">
              <Card className="p-8">
                <p className="text-gray-600">No tutors available at the moment. Check back soon!</p>
              </Card>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {tutors.map((tutor) => (
                <TutorCard key={tutor.id} tutor={tutor} />
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Button 
              onClick={() => navigate('/search-results')}
              className="bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              View All Tutors
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gradient-to-br from-blue-50 via-white to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Success Stories</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4">What Our Users Say</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Real experiences from students and parents who transformed their learning journey
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="p-8 bg-white hover:shadow-xl transition-all duration-300 border-gray-100">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed mb-6 italic">"{testimonial.content}"</p>
                <div className="flex items-center space-x-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-teal-500 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Learning Experience?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of students achieving their academic goals with personalized tutoring
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate('/customer/register')} className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300">
              Find a Tutor
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button variant="outline" onClick={() => navigate('/tutor/register')} className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg backdrop-blur-sm">
              Become a Tutor
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;